// ================================================================
// src/core/books/apiClient.js
// نسخه‌ی نهایی و کامل — رفع مشکل sync بین کاربران مختلف
// ================================================================
// این نسخه:
//   ۱. API_BASE را با تشخیص خودکار تعیین می‌کند
//      - REACT_APP_API_URL اگر URL کامل بود → همان
//      - localhost / 127.0.0.1 → http://localhost:3001
//      - شبکه‌ی خصوصی (192.168 / 10 / 172.16-31) → http://<host>:3001
//      - production واقعی → /api (nginx proxy به :3001)
//   ۲. FILE_API را با همان منطق (port 3002 / /upload) تعیین می‌کند
//   ۳. پاسخ SPA fallback (HTML) را تشخیص می‌دهد و به‌جای
//      «موفقیت دروغین»، null/false برمی‌گرداند
//   ۴. قرارداد خروجی هر تابع دقیقاً مثل نسخه‌ی قبل است
//      → هیچ مصرف‌کننده‌ای نمی‌شکند
// ================================================================

// ──────────────────────────────────────────────────────────────
// helpers
// ──────────────────────────────────────────────────────────────
import { getToken } from '../../utils/secureStorage';


const isLocalHostname = (h) =>
  h === 'localhost' || h === '127.0.0.1' || h === '::1' ||
  /^192\.168\./.test(h) || /^10\./.test(h) || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(h);

const detectApiBase = () => {
  // 🆕 اولویت با آدرس فعلی مرورگر (برای شبکه محلی)
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (isLocalHostname(h)) return `http://${h}:3001`;
  }
  const configured = process.env.REACT_APP_API_URL;
  if (configured && /^https?:\/\//i.test(configured)) {
    return configured.replace(/\/+$/, '');
  }
  return configured || '/api';
};

const detectFileApi = () => {
  // 🆕 اولویت با آدرس فعلی مرورگر (برای شبکه محلی)
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (isLocalHostname(h)) return `http://${h}:3002`;
  }
  const configured = process.env.REACT_APP_FILE_API_URL;
  if (configured && /^https?:\/\//i.test(configured)) {
    return configured.replace(/\/+$/, '');
  }
  return configured || '/upload';
};

export const API_BASE = detectApiBase();
export const FILE_API = detectFileApi();

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.info('[apiClient] API_BASE =', API_BASE, '| FILE_API =', FILE_API);
}

const dbg = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[apiClient]', ...args);
  }
};

async function fetchWithTimeout(url, opts = {}, timeoutMs = 15000) {
  // 🆕 گارد آفلاین: اگر اینترنت نیست، اصلا درخواست نده
  if (!navigator.onLine) {
    throw new Error('OFFLINE');
  }

  // ✅ اضافه کردن توکن JWT به تمام درخواست‌ها
  const token = getToken();
  const headers = {
    ...(opts.headers || {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, headers, signal: ctrl.signal });
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('TIMEOUT');
    throw err;
  } finally {
    clearTimeout(t);
  }
}

// ──────────────────────────────────────────────────────────────
// safeJson — پاسخ را بدون شکنندگی پارس می‌کند
//   • 2xx + application/json → { ok:true, data }
//   • 2xx + بدنه‌ی خالی → { ok:true, data:null }   (DELETE)
//   • 2xx + HTML/متنی → { ok:false, reason:'non_json' }  (SPA fallback)
//   • JSON نامعتبر → { ok:false, reason:'invalid_json' }
// ──────────────────────────────────────────────────────────────
async function safeJson(response) {
  const ct = (response.headers.get('content-type') || '').toLowerCase();

  // مسیر سریع: JSON استاندارد
  if (ct.includes('application/json')) {
    try {
      const data = await response.json();
      return { ok: true, data };
    } catch (e) {
      return { ok: false, reason: 'invalid_json', error: e.message };
    }
  }

  // مسیر محتاط: بدنه خالی (DELETE) یا HTML (SPA fallback)
  let text = '';
  try { text = await response.text(); }
  catch (e) { return { ok: false, reason: 'body_read_failed', error: e.message }; }

  if (!text || text.trim() === '') {
    return { ok: true, data: null };
  }

  const trimmed = text.trimStart();
  if (trimmed.startsWith('<') || ct.includes('text/html')) {
    return { ok: false, reason: 'non_json', contentType: ct || 'unknown' };
  }

  try {
    return { ok: true, data: JSON.parse(text) };
  } catch {
    return { ok: false, reason: 'invalid_json' };
  }
}

// ──────────────────────────────────────────────────────────────
// api
// ──────────────────────────────────────────────────────────────

export const api = {
  /** GET /collection?query → array | null */
  async getAll(col, query = '') {
    try {
      const url = `${API_BASE}/${col}${query ? `?${query}` : ''}`;
      const r = await fetchWithTimeout(url);
      if (!r.ok) { dbg(`getAll ${col}: status=${r.status}`); return null; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`getAll ${col}: ${parsed.reason}`); return null; }
      return Array.isArray(parsed.data) ? parsed.data : null;
    } catch (e) {
      dbg(`getAll ${col} failed:`, e.message);
      return null;
    }
  },

  /** GET /collection/:id → object | null */
  async getOne(col, id) {
    try {
      const r = await fetchWithTimeout(
        `${API_BASE}/${col}/${encodeURIComponent(id)}`
      );
      if (!r.ok) { dbg(`getOne ${col}/${id}: status=${r.status}`); return null; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`getOne ${col}/${id}: ${parsed.reason}`); return null; }
      return parsed.data && typeof parsed.data === 'object' ? parsed.data : null;
    } catch (e) {
      dbg(`getOne ${col}/${id} failed:`, e.message);
      return null;
    }
  },

  /** POST /collection → object | null */
  async post(col, body) {
    try {
      const r = await fetchWithTimeout(`${API_BASE}/${col}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { dbg(`post ${col}: status=${r.status}`); return null; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`post ${col}: ${parsed.reason}`); return null; }
      return parsed.data && typeof parsed.data === 'object' ? parsed.data : null;
    } catch (e) {
      dbg(`post ${col} failed:`, e.message);
      return null;
    }
  },

  /** PUT /collection/:id → boolean */
  async put(col, id, body) {
    try {
      const r = await fetchWithTimeout(
        `${API_BASE}/${col}/${encodeURIComponent(id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!r.ok) { dbg(`put ${col}/${id}: status=${r.status}`); return false; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`put ${col}/${id}: ${parsed.reason}`); return false; }
      return true;
    } catch (e) {
      dbg(`put ${col}/${id} failed:`, e.message);
      return false;
    }
  },

  /** PATCH /collection/:id → boolean */
  async patch(col, id, body) {
    try {
      const r = await fetchWithTimeout(
        `${API_BASE}/${col}/${encodeURIComponent(id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!r.ok) { dbg(`patch ${col}/${id}: status=${r.status}`); return false; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`patch ${col}/${id}: ${parsed.reason}`); return false; }
      return true;
    } catch (e) {
      dbg(`patch ${col}/${id} failed:`, e.message);
      return false;
    }
  },

  /** DELETE /collection/:id → boolean */
  async del(col, id) {
    try {
      const r = await fetchWithTimeout(
        `${API_BASE}/${col}/${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );
      if (!r.ok) { dbg(`del ${col}/${id}: status=${r.status}`); return false; }
      const parsed = await safeJson(r);
      if (!parsed.ok) { dbg(`del ${col}/${id}: ${parsed.reason}`); return false; }
      return true;
    } catch (e) {
      dbg(`del ${col}/${id} failed:`, e.message);
      return false;
    }
  },

  // 🆕 حذف idempotent — 404 یعنی «قبلاً حذف شده» = موفقیت
  // برای صف حذف remoteSync استفاده می‌شود تا در صورت حذف همزمان از دو دستگاه، loop نشود
  async delSafe(col, id) {
    try {
      const r = await fetchWithTimeout(
        `${API_BASE}/${col}/${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );
      if (r.status === 404) return true;
      if (!r.ok) { dbg(`delSafe ${col}/${id}: status=${r.status}`); return false; }
      return true;
    } catch (e) {
      dbg(`delSafe ${col}/${id} failed:`, e.message);
      return false;
    }
  },

  /**
   * 🆕 health check برای دیباگ
   * در کنسول: (await import('./core/books/apiClient.js')).api.health()
   */
  async health() {
    try {
      const r = await fetchWithTimeout(`${API_BASE}/__health`, {}, 5000);
      if (!r.ok) return { ok: false, status: r.status };
      const parsed = await safeJson(r);
      return parsed.ok ? parsed.data : { ok: false, reason: parsed.reason };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  },
};

// ──────────────────────────────────────────────────────────────
// uploadFile — آپلود فایل به سرور ۳۰۰۲
// ──────────────────────────────────────────────────────────────
export async function uploadFile(file, { teacherId, examId, type, questionIndex, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('image', file);
    if (teacherId) fd.append('teacherId', teacherId);
    if (examId) fd.append('examId', examId);
    if (type) fd.append('type', type);
    if (questionIndex != null) fd.append('questionIndex', questionIndex);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${FILE_API}/api/upload`);
    
    // ✅ ارسال توکن JWT به همراه آپلود
    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    // 🆕 پشتیبانی از لغو آپلود
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('upload_aborted'));
      });
    }

    // 🆕 گزارش دقیق: percent + loaded + total
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({
          percent: Math.round((e.loaded / e.total) * 100),
          loaded: e.loaded,
          total: e.total,
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const payload = JSON.parse(xhr.responseText);
          if (!payload.success) reject(new Error(payload.error || 'upload_failed'));
          else resolve(payload.path);
        } catch (e) {
          reject(new Error('upload_invalid_json'));
        }
      } else {
        reject(new Error(`upload_failed_${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('upload_network_error'));
    xhr.send(fd);
  });
}

export default api;